import React, { useState, useEffect } from 'react';

import styled from 'styled-components';

import Select from 'react-select';
import { Check, Cross, Help } from '@core-ds/icons/24/';
import { color } from '@core-ds/primitives';

const enum CompatibilityType {
   True = 'true',
   False = 'false',
   Maybe = 'maybe',
}

interface DeviceCompatibilityIconProps {
   compatible: CompatibilityType;
}

const CompatibilityIcon = styled.div.attrs((props: DeviceCompatibilityIconProps) => ({
   compatible: props.compatible ?? CompatibilityType.Maybe,
}))`
   position: absolute;
   height: 36px;
   width: 36px;
   border-radius: 50%;
   top: 17px;
   left: -19px;
   padding: 6px;
   color: ${color.white};

   background-color: ${props =>
      props.compatible === CompatibilityType.True
         ? color.green
         : props.compatible === CompatibilityType.False
            ? color.red
            : color.yellow};
`;

const DeviceCompatibilityIcon = ({ compatible }: DeviceCompatibilityIconProps) => {
   return (
      <CompatibilityIcon compatible={compatible}>
         {compatible === CompatibilityType.True ? (
            <Check />
         ) : compatible === CompatibilityType.False ? (
            <Cross />
         ) : (
            <Help />
         )}
      </CompatibilityIcon>
   );
};

const CompatibilityHeader = styled.h3`
   margin: 0;
   margin-bottom: 5px;
   color: ${color.black};
`;

interface DeviceCompatibilityHeaderProps {
   compatible: CompatibilityType;
   doesWikiHaveVariants: boolean;
}

const DeviceCompatibilityHeader = ({
   compatible,
   doesWikiHaveVariants,
}: DeviceCompatibilityHeaderProps) => {
   return (
      <CompatibilityHeader>
         {compatible === CompatibilityType.True ? (
            _js('This works with your:')
         ) : compatible === CompatibilityType.False ? (
            _js("This won't work with your:")
         ) : (
            <>
               {_js('This part might fit.')}
               {(doesWikiHaveVariants ? (
                  <p>{_js('We need a little more information')}</p>
               ) : (
                  <p>{_js("We don't have enough information")}</p>
               ))}
            </>
         )}
      </CompatibilityHeader>
   );
};

interface DevicePartCompatibilityProps {
   productcode: number;
};

interface Device {
   // wiki/device title
   label: string;
   // value is wikkid in form of
   //   xxx- for nonvariants
   //   xxx-xxx for variants
   value: string;
   isCompatible: CompatibilityType;
};

interface TopicVariantOptions {
   [variantid: string]: Device;
};

interface DropdownTopicVariantsResponseType {
   // first half of device variants' wikiid as key
   [wikiid: string]: TopicVariantOptions;
};

const DevicePartCompatibility = ({ productcode }: DevicePartCompatibilityProps) => {
   const [dropdownTopicVariants, setDropdownTopicVariants] = useState<
      DropdownTopicVariantsResponseType
   >();
   const [dropdownTopics, setDropdownTopics] = useState<Array<Device>>();

   const [activeTopicWikiid, setActiveTopicWikiid] = useState<string>();
   const [activeVariantWikiid, setActiveVariantWikiid] = useState<string>();

   useEffect(() => {
      new Request.API_2_0(`store/user/workbench_compatibility/${productcode}`, {
         method: 'GET',
      })
         .send()
         .then(
            (response: {
               dropdownTopicVariants: DropdownTopicVariantsResponseType;
               dropdownTopics: Device[];
               activeTopicWikiid: string;
            }) => {
               const { dropdownTopicVariants, dropdownTopics, activeTopicWikiid } = response;

               setDropdownTopicVariants(dropdownTopicVariants);
               setDropdownTopics(dropdownTopics);

               setActiveTopicWikiid(activeTopicWikiid);
               setActiveVariantWikiid(activeTopicWikiid + '-default');
            }
         );
   }, [productcode]);

   // all wikiids passed will be of form xxx- or xxx-xxx so we can
   // use for both topic and variant wikiid changings
   const changeSelectedTopic = (selectedTopic: Device) => {
      const selectedWikiid = selectedTopic.value;
      const splitWikiid = selectedWikiid.split('-');
      if (!splitWikiid[1]) {
         // means wikiid of form xxx-
         setActiveTopicWikiid(splitWikiid[0]);
         setActiveVariantWikiid(splitWikiid[0] + '-default');
      } else {
         setActiveTopicWikiid(splitWikiid[0]);
         setActiveVariantWikiid(selectedWikiid);
      }
   };

   const activeTopic =
      dropdownTopics && activeTopicWikiid
         ? dropdownTopics.find(o => o.value.includes(activeTopicWikiid))
         : undefined;

   const doesWikiHaveVariants: boolean =
      dropdownTopicVariants && typeof dropdownTopicVariants[activeTopicWikiid] !== 'undefined';

   const activeVariant: Device | undefined =
      doesWikiHaveVariants && dropdownTopicVariants[activeTopicWikiid][activeVariantWikiid]
         ? dropdownTopicVariants[activeTopicWikiid][activeVariantWikiid]
         : undefined;

   const variantDropdownOptions: Device[] = doesWikiHaveVariants
      ? (Object as any).values(dropdownTopicVariants[activeTopicWikiid])
      : [];

   // if active topic is selected see if active variant is as well
   // take active variant compatibility whenever available, otherwise use active topic
   const isActiveCompatible = (): (CompatibilityType | null) =>
      activeTopic
         ? activeVariant && activeVariant.value.startsWith(activeTopic.value)
            ? activeVariant.isCompatible
            : activeTopic.isCompatible
         : null;

   const CompatibilityCheckContainer: any = styled.div`
      z-index: 1;
      position: relative;
      display: flex;
      flex-direction: column;
      width: 100%;
      margin: auto;
      margin-bottom: 30px;
      padding: 24px 32px 24px 32px;
      background-color: ${color.white};
      border: 1px solid ${color.gray3};
      border-radius: 4px;

      @media (max-width: 599px) {
         margin-bottom: 0;
      }
   `;

   const CompatibilityHeader: any = styled.div`
      display: inline-block;
      margin-bottom: 8px;
   `;

   const WorkbenchVariationsDropdown: any = styled(Select)`
      margin-top: 1em;
   `;

   const AddDeviceButtonContainer: any = styled.div`
      display: flex;
      margin-top: 16px;

      @media (max-width: 1000px) {
         display: none !important;
      }
   `;

   const AddDeviceButton: any = styled.h5`
      font-weight: normal;
      :hover {
         text-decoration: none;
      }
   `;

   const customStyles = {
      option: (provided) => ({ ...provided, fontSize: 15 }),
      singleValue: (provided) => ({ ...provided, fontSize: 15})
   };

   return !activeTopic ? (
      <div className="missing-device-part-compatibility-checker"></div>
   ) : (
         <CompatibilityCheckContainer className="device-part-compatibility-checker">
            <CompatibilityHeader>
               <DeviceCompatibilityIcon compatible={isActiveCompatible()} />
               <DeviceCompatibilityHeader
                  compatible={isActiveCompatible()}
                  doesWikiHaveVariants={doesWikiHaveVariants}
               />
            </CompatibilityHeader>
            <Select
               isSearchable={false}
               className="compatibility-topic-dropdown"
               value={activeTopic}
               options={dropdownTopics}
               onChange={changeSelectedTopic}
               styles={customStyles}
            />
            {doesWikiHaveVariants && activeVariant && (
               <WorkbenchVariationsDropdown
                  isSearchable={false}
                  className="compatibility-variation-dropdown"
                  value={activeVariant}
                  options={variantDropdownOptions}
                  onChange={changeSelectedTopic}
                  styles={customStyles}
               />
            )}
            <AddDeviceButtonContainer>
               <AddDeviceButton>
                  {/* workaround for now as I migrate workbench to React */}
                  <a onClick={() => (window as any).Workbench.selectDevice()}>
                     {_js('Add Another Device')}
                  </a>
               </AddDeviceButton>
            </AddDeviceButtonContainer>
         </CompatibilityCheckContainer>
      );
};

export default DevicePartCompatibility;
